import { getAuthData, getAuthUserId } from "../auth/session.js";

function responseError(status) {
  if (status === 404 || status === 405) return "Раздел события ещё не подключён на сервере. Требуется обновить и перезапустить Mini App API";
  if (status === 401 || status === 403) return "Сессия истекла. Войдите в аккаунт заново";
  if (status >= 500) return "Сервер события временно недоступен. Попробуйте позже";
  return "Не удалось загрузить событие. Попробуйте ещё раз";
}

function validChest(chest) {
  if (chest === undefined) return true;
  if (!chest || typeof chest.enabled !== "boolean") return false;
  if (!chest.enabled) return true;
  const bounds = value => Array.isArray(value) && value.length === 2 && value.every(n => Number.isSafeInteger(n) && n >= 0) && value[0] <= value[1];
  const reward = value => value && Number.isSafeInteger(value.level) && value.level >= 1
    && bounds(value.spins) && bounds(value.gems) && bounds(value.currency)
    && [value.limitedChance, value.secondLimitedChance].every(n => Number.isFinite(n) && n >= 0 && n <= 100);
  return typeof chest.name === "string" && Number.isSafeInteger(chest.level) && chest.level >= 1
    && Number.isSafeInteger(chest.maxLevel) && chest.level <= chest.maxLevel
    && ["opened", "canUpgrade", "canOpen"].every(key => typeof chest[key] === "boolean")
    && reward(chest.current) && chest.current.level === chest.level
    && (chest.next === null ? chest.level === chest.maxLevel && chest.upgradeCost === null
      : reward(chest.next) && chest.next.level === chest.level + 1 && Number.isSafeInteger(chest.upgradeCost) && chest.upgradeCost >= 0)
    && (chest.openingNotice === null || typeof chest.openingNotice === "string");
}

function validState(result) {
  if (typeof result.available !== "boolean" || !Array.isArray(result.offers)) return false;
  if (result.shop !== undefined && (!Array.isArray(result.shop) || !result.shop.every(item => item
    && typeof item.id === "string" && ["spins", "cases", "crystals"].includes(item.resource)
    && Number.isSafeInteger(item.amount) && item.amount > 0 && Number.isSafeInteger(item.price) && item.price >= 0))) return false;
  if (!result.available) return result.event === null && result.offers.length === 0;
  const event = result.event;
  return event && typeof event.id === "string" && typeof event.title === "string"
    && typeof event.active === "boolean" && Number.isFinite(event.currency) && validChest(event.chest)
    && typeof event.currencyName === "string" && typeof event.currencyEmoji === "string"
    && [event.start, event.end].every(value => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)))
    && result.offers.every(offer => offer && ["stock", "set"].includes(offer.key)
      && typeof offer.title === "string" && offer.rewards && typeof offer.rewards === "object" && !Array.isArray(offer.rewards)
      && Object.values(offer.rewards).every(value => Number.isFinite(value) && value >= 0)
      && Number.isFinite(offer.price) && offer.price >= 0
      && ["enabled", "coinEnabled", "referralEnabled", "canBuy", "canClaim", "claimed"].every(key => typeof offer[key] === "boolean")
      && Number.isFinite(offer.referrals) && Number.isFinite(offer.referralTarget)
      && (offer.remaining === null || Number.isFinite(offer.remaining)));
}

export async function eventRequest(operation, body = {}, signal) {
  const initData = getAuthData();
  if (!initData) throw new Error("Войдите в аккаунт, чтобы открыть событие");
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) controller.abort();
  const timeout = setTimeout(abort, 12000);
  try {
    const response = await fetch(`/api/events/halloween/${operation}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...body, initData }), signal: controller.signal,
    });
    const raw = await response.text();
    let result;
    try { result = JSON.parse(raw); }
    catch { throw new Error(responseError(response.status)); }
    if (!result || typeof result !== "object" || Array.isArray(result)) throw new Error(responseError(response.status));
    if (!response.ok) {
      const error = new Error(typeof result.error === "string" && result.error.trim() ? result.error : responseError(response.status));
      error.status = response.status;
      throw error;
    }
    if (operation === "state" ? !validState(result) : result.ok !== true) {
      throw new Error("Сервер вернул неполные данные события. Попробуйте обновить страницу");
    }
    if (operation === "chest" && body.action === "open" && (!Array.isArray(result.rewards)
      || !result.rewards.every(item => item && (item.type === "card"
        ? typeof item.name === "string" && Number.isSafeInteger(item.number) && item.number > 0
          && item.rarity === "LIMITED" && Number.isFinite(item.points) && item.points >= 0
          && [item.mediaPath, item.thumbnailPath].every(path => path == null || typeof path === "string")
        : ["spins", "crystals", "currency"].includes(item.type) && Number.isSafeInteger(item.amount) && item.amount > 0)))) {
      throw new Error("Сервер вернул неполный результат рундука. Повторите запрос, чтобы восстановить награды");
    }
    return result;
  } catch (error) {
    if (error.name === "AbortError" || error instanceof TypeError) throw new Error("Связь прервалась. Попробуйте ещё раз");
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}

// Persist before sending: revisiting the hub after a lost response reuses the receipt.
export function purchaseRequest(eventId, key, mode) {
  const storageKey = `metacard-event-offer:${getAuthUserId()}:${eventId}:${key}:${mode}`;
  let requestId = sessionStorage.getItem(storageKey);
  if (!requestId) {
    requestId = crypto.randomUUID();
    sessionStorage.setItem(storageKey, requestId);
  }
  return { eventId, key, mode, requestId, storageKey };
}

export function pendingChestRequest(eventId) {
  const storageKey = `metacard-event-chest:${getAuthUserId()}:${eventId}`;
  const raw = sessionStorage.getItem(storageKey);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (value.eventId === eventId && ["upgrade", "open"].includes(value.action)
      && Number.isSafeInteger(value.level) && value.level >= 1 && value.level <= 30
      && typeof value.requestId === "string") return { ...value, storageKey };
  } catch { /* Ignore a damaged local receipt. */ }
  sessionStorage.removeItem(storageKey);
  return null;
}

export function chestRequest(eventId, action, level) {
  const pending = pendingChestRequest(eventId);
  if (pending) return pending;
  const storageKey = `metacard-event-chest:${getAuthUserId()}:${eventId}`;
  const request = { eventId, action, level, requestId: crypto.randomUUID() };
  sessionStorage.setItem(storageKey, JSON.stringify(request));
  return { ...request, storageKey };
}
